import Swiper from './swiper-bundle.esm.browser.min.js';
import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';

const PLAY_ICON =
  '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><path d="M6 4.5v11l9-5.5z" fill="currentColor"/></svg>';
const AUTO_ICON =
  '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="6" cy="10" r="1.5" fill="currentColor"/><circle cx="10" cy="10" r="1.5" fill="currentColor"/><circle cx="14" cy="10" r="1.5" fill="currentColor"/></svg>';

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.mainEl = this.querySelector('[data-gallery-main]');
    this.list = this.querySelector('[data-media-list]');
    this.thumbsEl = this.querySelector('[data-gallery-thumbs]');
    this.thumbsList = this.querySelector('[data-thumbs-list]');
    this.scrollbarEl = this.querySelector('[data-gallery-scrollbar]');
    this.currentEl = this.querySelector('[data-gallery-current]');
    this.totalEl = this.querySelector('[data-gallery-total]');

    this.build();
    this.initLightbox();

    this.listObserver = new MutationObserver(() => this.rebuild());
    this.listObserver.observe(this.list, { childList: true });
  }

  disconnectedCallback() {
    this.listObserver?.disconnect();
    this.destroySwipers();
    this.lightbox?.destroy();
  }

  get slides() {
    return Array.from(this.list.children).filter((el) => el.hasAttribute('data-media-id'));
  }

  build() {
    const slides = this.slides;
    this.toggleAttribute('data-single', slides.length < 2);
    this.totalEl.textContent = slides.length;
    this.renderThumbs(slides);

    this.thumbs = new Swiper(this.thumbsEl, {
      direction: 'vertical',
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchSlidesProgress: true,
      a11y: { enabled: false },
      mousewheel: { forceToAxis: true },
      on: {
        afterInit: (swiper) => this.updateThumbEdges(swiper),
        progress: (swiper) => this.updateThumbEdges(swiper),
        resize: (swiper) => this.updateThumbEdges(swiper),
      },
    });

    this.main = new Swiper(this.mainEl, {
      slidesPerView: 1,
      spaceBetween: 4,
      breakpoints: {
        750: { slidesPerView: 2, spaceBetween: 8 },
        1000: { slidesPerView: 1, spaceBetween: 4 },
      },
      speed: 400,
      threshold: 10,
      grabCursor: true,
      watchOverflow: true,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: { enabled: true, slideRole: 'group' },
      scrollbar: { el: this.scrollbarEl, draggable: true },
      thumbs: { swiper: this.thumbs, autoScrollOffset: 1 },
      on: {
        afterInit: () => this.onSlideChange(),
        slideChange: () => this.onSlideChange(),
        reachEnd: () => this.onSlideChange(),
        breakpoint: () => this.onSlideChange(),
      },
    });
  }

  rebuild() {
    const activeId = this.pendingMediaId ?? this.slides[this.main?.activeIndex]?.dataset.mediaId;
    this.destroySwipers();
    this.build();
    this.goToMedia(activeId, 0);
  }

  destroySwipers() {
    this.main?.destroy(true, true);
    this.thumbs?.destroy(true, true);
    this.main = null;
    this.thumbs = null;
  }

  setActiveMedia(mediaId) {
    this.pendingMediaId = mediaId;
    this.goToMedia(mediaId);
    queueMicrotask(() => {
      this.pendingMediaId = null;
    });
  }

  goToMedia(mediaId, speed) {
    if (!mediaId || !this.main) return;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index >= 0) this.main.slideTo(index, speed);
  }

  renderThumbs(slides) {
    const items = slides.map((slide, index) => {
      const { thumb, thumbAlt, thumbType } = slide.dataset;
      const isVideo = thumbType === 'video' || thumbType === 'external_video';
      const label = (isVideo ? this.dataset.videoThumbLabel : this.dataset.thumbLabel).replace('[index]', index + 1);

      const item = document.createElement('li');
      item.className = 'product-gallery__thumb swiper-slide';
      item.tabIndex = 0;
      item.setAttribute('role', 'button');
      item.setAttribute('aria-label', label);
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.main.slideTo(index);
      });

      const image = document.createElement('img');
      image.src = thumb;
      image.alt = thumbAlt;
      image.width = 80;
      image.height = 100;
      image.loading = 'lazy';
      item.append(image);

      if (isVideo || thumbType === 'auto') {
        const badge = document.createElement('span');
        badge.className = 'product-gallery__thumb-badge';
        badge.innerHTML = isVideo ? PLAY_ICON : AUTO_ICON;
        item.append(badge);
      }
      return item;
    });

    this.thumbsList.replaceChildren(...items);
  }

  updateThumbEdges(swiper) {
    const track = this.thumbsEl.parentElement;
    track.classList.toggle('is-at-start', swiper.isBeginning);
    track.classList.toggle('is-at-end', swiper.isEnd);
  }

  onSlideChange() {
    if (!this.main) return;
    this.currentEl.textContent = this.main.isEnd ? this.slides.length : this.main.activeIndex + 1;
    this.syncVideos();
  }

  syncVideos() {
    this.slides.forEach((slide, index) => {
      slide.querySelectorAll('.product-gallery__video video').forEach((video) => {
        if (index === this.main.activeIndex) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  initLightbox() {
    this.lightbox = new PhotoSwipeLightbox({
      gallery: this.list,
      children: '.product-gallery__zoom',
      pswpModule: () => import('./photoswipe.esm.min.js'),
      bgOpacity: 1,
      showHideAnimationType: 'fade',
      imageClickAction: 'zoom',
      tapAction: 'toggle-controls',
      doubleTapAction: 'zoom',
    });

    this.lightbox.on('beforeOpen', () => {
      this.querySelectorAll('auto-scroll-slider').forEach((slider) => {
        slider.pauseAutoplay?.();
        slider.pauseProgressBar?.();
      });
    });

    this.lightbox.on('close', () => {
      const element = this.lightbox.pswp?.currSlide?.data?.element;
      const slide = element?.closest('[data-media-id]');
      if (slide) this.goToMedia(slide.dataset.mediaId);
      document.dispatchEvent(new CustomEvent('modalClosed'));
    });

    this.lightbox.init();
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
